import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Lock, Eye, EyeOff, ArrowRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { IconInput } from '../../components/ui/icon-input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { getApiUrl } from '../../utils/authUtils';

const API = getApiUrl();

const passwordMeetsRules = (value) => {
  if (!value || value.length < 8) return false;
  if (!/[A-Za-z]/.test(value)) return false;
  if (!/\d/.test(value)) return false;
  return true;
};

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tokenStatus, setTokenStatus] = useState('checking');
  const [tokenValid, setTokenValid] = useState(false);

  useEffect(() => {
    if (!token) {
      setTokenStatus('missing');
      setTokenValid(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(`${API}/auth/reset-password/verify`, {
          params: { token },
          withCredentials: true,
        });
        if (cancelled) return;
        const valid = Boolean(response.data?.valid);
        setTokenValid(valid);
        setTokenStatus(response.data?.status || (valid ? 'pending' : 'invalid'));
      } catch {
        if (!cancelled) {
          setTokenValid(false);
          setTokenStatus('invalid');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!token || !tokenValid) {
      toast.error('Invalid reset link. Please request a new password reset.');
      return;
    }
    if (!passwordMeetsRules(password)) {
      toast.error('Password must be at least 8 characters with one letter and one number.');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/auth/reset-password`,
        { token, password },
        { withCredentials: true }
      );
      toast.success('Your password has been reset. Please log in.');
      navigate('/login', { replace: true, state: { resetSuccess: true } });
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (!error.response) {
        toast.error('Unable to reach the server. Please try again.');
      } else {
        toast.error(detail || 'Reset failed. Please request a new link.');
      }
    } finally {
      setLoading(false);
    }
  };

  const tokenInvalidMessage =
    tokenStatus === 'expired'
      ? 'This reset link has expired.'
      : tokenStatus === 'used'
        ? 'This reset link has already been used.'
        : 'This reset link is invalid or missing.';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-slate-900">Choose a new password</h1>
          <p className="text-slate-600 mt-2">Enter and confirm your new password below.</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
          {tokenStatus === 'checking' ? (
            <p className="text-center text-slate-600">Checking reset link...</p>
          ) : !token || !tokenValid ? (
            <div className="space-y-4 text-center">
              <p className="text-slate-700">{tokenInvalidMessage}</p>
              <Link to="/forgot-password">
                <Button className="w-full bg-emerald-600 hover:bg-emerald-700">
                  Request a new reset link
                </Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="password">New password</Label>
                <div className="relative">
                  <IconInput
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="At least 8 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    icon={Lock}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <p className="text-xs text-slate-500">Use at least 8 characters with one letter and one number.</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm password</Label>
                <IconInput
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Repeat password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  icon={Lock}
                  required
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                disabled={loading}
              >
                {loading ? 'Updating...' : 'Update password'}
                {!loading && <ArrowRight className="ml-2 h-4 w-4" />}
              </Button>
            </form>
          )}

          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm text-emerald-600 hover:text-emerald-700">
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
